"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

export default function InfinityScrollSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Track scroll progress within this container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"],
  });

  // Simplified on mobile - all animations disabled, static display
  const pathLength = useTransform(scrollYProgress, [0, 0.833], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.417, 0.833], [0.8, 1.2, 1]);
  const popupOpacity = useTransform(scrollYProgress, [0.125, 0.208, 0.833, 0.9], [0, 1, 1, 0]);
  const popupY = useTransform(scrollYProgress, [0.125, 0.208], [20, 0]);
  const popup2Opacity = useTransform(scrollYProgress, [0.208, 0.292, 0.833, 0.9], [0, 1, 1, 0]);
  const popup2Y = useTransform(scrollYProgress, [0.208, 0.292], [20, 0]);
  const popup3Opacity = useTransform(scrollYProgress, [0.458, 0.542, 0.833, 0.9], [0, 1, 1, 0]);
  const popup3Y = useTransform(scrollYProgress, [0.458, 0.542], [20, 0]);
  const popup4Opacity = useTransform(scrollYProgress, [0.583, 0.667, 0.833, 0.9], [0, 1, 1, 0]);
  const popup4Y = useTransform(scrollYProgress, [0.583, 0.667], [20, 0]);
  const finalTitleOpacity = useTransform(scrollYProgress, [0.833, 0.95], [0, 1]);
  const finalTitleScale = useTransform(scrollYProgress, [0.833, 0.95], [0.3, 1]);
  const infinityOpacity = useTransform(scrollYProgress, [0, 0.05, 0.833, 0.95], [0, 1, 1, 0]);
  const glowIntensity = useTransform(scrollYProgress, [0, 0.25, 0.583, 0.833], [0.3, 1, 1, 0.5]);

  // Infinity symbol path (mathematical infinity loop)
  const infinityPath =
    "M 100 200 C 100 100, 200 100, 250 200 C 300 300, 400 300, 400 200 C 400 100, 300 100, 250 200 C 200 300, 100 300, 100 200 Z";

  return (
    <div ref={containerRef} className="relative pt-20" style={{ height: isMobile ? "500vh" : "840vh" }}>
      {/* Fixed viewport for the animation */}
      <div className="sticky top-0 h-screen w-full flex items-center justify-center bg-black overflow-hidden -mt-20">
        {/* Progress Bar - Positioned below nav with better visibility */}
        <div className="absolute top-20 left-0 right-0 h-2 bg-black/80 backdrop-blur-sm z-50 border-y border-orange-500/20">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-300"
            style={{
              scaleX: scrollYProgress,
              transformOrigin: "0%",
              boxShadow: "0 0 15px rgba(249, 115, 22, 0.9), 0 0 30px rgba(249, 115, 22, 0.6)",
            }}
          />
        </div>

        {/* Background grid effect */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />

        {/* First popup card - top left */}
        <motion.div
          style={{ opacity: popupOpacity, y: popupY }}
          className="absolute top-24 md:top-32 left-2 md:left-32 z-20 pointer-events-none"
        >
          <div className="relative">
            {/* Large number background */}
            <div className="absolute -top-4 md:-top-8 -left-2 md:-left-4 text-[80px] md:text-[140px] lg:text-[180px] font-bold text-orange-500/30 leading-none select-none">
              01
            </div>
            
            {/* Card content */}
            <div className="relative z-10 pt-12 md:pt-20">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Grift', sans-serif" }}>
                Embodiment
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-400 max-w-[200px] md:max-w-md" style={{ fontFamily: "'Grift', sans-serif" }}>
                Focus: natural movement,<br />
                grounding, posture, breath.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Second popup card - bottom right */}
        <motion.div
          style={{ opacity: popup2Opacity, y: popup2Y }}
          className="absolute bottom-4 md:bottom-12 right-2 md:right-32 z-20 pointer-events-none"
        >
          <div className="relative">
            {/* Large number background */}
            <div className="absolute -top-4 md:-top-8 -right-2 md:-right-4 text-[80px] md:text-[140px] lg:text-[180px] font-bold text-orange-500/30 leading-none select-none">
              02
            </div>
            
            {/* Card content */}
            <div className="relative z-10 pt-12 md:pt-20 text-right">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Grift', sans-serif" }}>
                Expression
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-400 max-w-[200px] md:max-w-md" style={{ fontFamily: "'Grift', sans-serif" }}>
                Focus: Dance what you feel.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Third popup card - top right */}
        <motion.div
          style={{ opacity: popup3Opacity, y: popup3Y }}
          className="absolute top-24 md:top-32 right-2 md:right-32 z-20 pointer-events-none"
        >
          <div className="relative">
            {/* Large number background */}
            <div className="absolute -top-4 md:-top-8 -right-2 md:-right-4 text-[80px] md:text-[140px] lg:text-[180px] font-bold text-orange-500/30 leading-none select-none">
              03
            </div>
            
            {/* Card content */}
            <div className="relative z-10 pt-12 md:pt-20 text-right">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Grift', sans-serif" }}>
                Connection
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-400 max-w-[200px] md:max-w-md" style={{ fontFamily: "'Grift', sans-serif" }}>
                Focus: Partner awareness, micro-leads,<br />
                frame sensitivity.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Fourth popup card - bottom left */}
        <motion.div
          style={{ opacity: popup4Opacity, y: popup4Y }}
          className="absolute bottom-4 md:bottom-12 left-2 md:left-32 z-20 pointer-events-none"
        >
          <div className="relative">
            {/* Large number background */}
            <div className="absolute -top-4 md:-top-8 -left-2 md:-left-4 text-[80px] md:text-[140px] lg:text-[180px] font-bold text-orange-500/30 leading-none select-none">
              04
            </div>
            
            {/* Card content */}
            <div className="relative z-10 pt-12 md:pt-20">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Grift', sans-serif" }}>
                Technique Integration
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-400 max-w-[200px] md:max-w-md" style={{ fontFamily: "'Grift', sans-serif" }}>
                Focus: timing, contraposition,<br />
                precision without rigidity.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Animated infinity symbol with enhanced glow */}
        <motion.div
          style={{ scale, opacity: infinityOpacity, zIndex: 5 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <svg
            className="w-[90vw] h-auto md:w-[500px] md:h-[400px] max-w-[500px]"
            viewBox="0 0 500 400"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Multiple glow layers for enhanced effect */}
            <motion.path
              d={infinityPath}
              stroke="url(#gradient-glow)"
              strokeWidth="20"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              style={{ opacity: useTransform(glowIntensity, (v) => v * 0.2) }}
              filter="blur(20px)"
            />
            
            <motion.path
              d={infinityPath}
              stroke="url(#gradient-glow)"
              strokeWidth="12"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              style={{ opacity: useTransform(glowIntensity, (v) => v * 0.4) }}
              filter="blur(10px)"
            />

            {/* Main animated path with glow */}
            <motion.path
              d={infinityPath}
              stroke="url(#gradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              style={{ 
                pathLength,
                filter: "drop-shadow(0 0 15px rgba(249, 115, 22, 0.8)) drop-shadow(0 0 30px rgba(249, 115, 22, 0.6))"
              }}
            />

            {/* Gradient definitions */}
            <defs>
              <linearGradient
                id="gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="50%" stopColor="#f97316" />
                <stop offset="100%" stopColor="#ffffff" />
              </linearGradient>
              <linearGradient
                id="gradient-glow"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="50%" stopColor="#f97316" />
                <stop offset="100%" stopColor="#ffffff" />
              </linearGradient>
            </defs>
          </svg>

        </motion.div>

        {/* Final Title - Endless Academy Logo */}
        <motion.div
          style={{ 
            opacity: finalTitleOpacity, 
            scale: finalTitleScale 
          }}
          className="absolute z-30 pointer-events-none px-0 md:px-4 flex items-center justify-center"
        >
          {/* NOTE: Logo should be optimized to ~512x220px (2x retina: 1024x440px) for h-16 md:h-24 lg:h-32 display */}
          <img 
            src="/assets/logo_orange_endlessacademy.png" 
            alt="Endless Academy" 
            className="h-16 md:h-24 lg:h-32 w-auto mx-auto"
            width={512}
            height={220}
            loading="lazy"
            style={{
              filter: "drop-shadow(0 0 20px rgba(249, 115, 22, 0.6)) drop-shadow(0 0 40px rgba(249, 115, 22, 0.4))"
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}